import type {
  CreateWatcherTokenRequest,
  CreateWatcherTokenResponse,
  RevokeWatcherTokenResponse,
  WatcherTokenInfo,
  WatcherTokenListResponse,
} from '@brimstone/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import {
  DataGrid,
  DataRow,
  EmptyState,
  Panel,
  PrimaryButton,
  SecondaryButton,
} from '../../components/ui';
import { api } from '../../lib/api';
import { queryKeys } from '../../lib/queries';
import { formatAge, formatDate } from '../../lib/time';

const COLUMNS = 'minmax(0, 1.6fr) minmax(0, 1fr) minmax(0, 1fr) 96px minmax(0, 1.4fr)';

/** Settings → Watcher tokens (SPEC §5.2): create (shown once), list, revoke. */
export function WatcherTokensPanel() {
  const queryClient = useQueryClient();
  const tokens = useQuery({
    queryKey: queryKeys.watcherTokens,
    queryFn: () => api<WatcherTokenListResponse>('/api/settings/watcher-tokens'),
  });
  const [name, setName] = useState('Gaming PC');
  const [created, setCreated] = useState<CreateWatcherTokenResponse | null>(null);

  const create = useMutation({
    mutationFn: (body: CreateWatcherTokenRequest) =>
      api<CreateWatcherTokenResponse>('/api/settings/watcher-tokens', {
        method: 'POST',
        json: body,
      }),
    onSuccess: (result) => {
      setCreated(result);
      void queryClient.invalidateQueries({ queryKey: queryKeys.watcherTokens });
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    create.mutate({ name: name.trim() });
  }

  const active = tokens.data?.tokens.filter((t) => !t.revokedAt).length;

  return (
    <Panel title="Watcher tokens" meta={active === undefined ? undefined : `${active} active`}>
      <p className="m-0 px-4 pt-3 text-secondary text-muted">
        The watcher on your gaming PC uploads SavedVariables with one of these tokens. A token is
        shown once, when it is created; paste it into the watcher with “Pair”.
      </p>

      <form onSubmit={submit} className="flex flex-wrap items-end gap-3 px-4 py-4">
        <label className="flex min-w-0 flex-[1_1_220px] flex-col gap-1">
          <span className="text-label uppercase tracking-label text-muted">Name</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={60}
            required
            className="min-h-11 border border-line bg-bg px-3 text-body"
          />
        </label>
        <PrimaryButton type="submit" disabled={create.isPending || name.trim() === ''}>
          {create.isPending ? 'Creating…' : 'Create token'}
        </PrimaryButton>
      </form>
      {create.isError && (
        <p className="m-0 px-4 pb-3 text-secondary text-danger">
          Couldn’t create the token: {create.error.message}
        </p>
      )}

      {created && <NewToken created={created} onDone={() => setCreated(null)} />}

      <div className="border-t border-line">
        {tokens.isPending ? (
          <EmptyState>Loading tokens…</EmptyState>
        ) : tokens.isError ? (
          <EmptyState>Couldn’t load tokens: {tokens.error.message}</EmptyState>
        ) : tokens.data.tokens.length === 0 ? (
          <EmptyState>No tokens yet. Create one to pair the watcher.</EmptyState>
        ) : (
          <DataGrid
            label="Watcher tokens"
            columns={COLUMNS}
            header={['Name', 'Created', 'Last seen', 'Status', '']}
          >
            {tokens.data.tokens.map((t) => (
              <TokenRow key={t.id} token={t} />
            ))}
          </DataGrid>
        )}
      </div>
    </Panel>
  );
}

function NewToken({
  created,
  onDone,
}: {
  created: CreateWatcherTokenResponse;
  onDone: () => void;
}) {
  const [copied, setCopied] = useState<'yes' | 'failed' | null>(null);

  async function copy() {
    try {
      await navigator.clipboard.writeText(created.token);
      setCopied('yes');
    } catch {
      setCopied('failed');
    }
  }

  return (
    <div className="mx-4 mb-4 border border-accent bg-accent-row px-4 py-3">
      <p className="m-0 text-secondary">
        New token for <strong className="font-semibold">{created.info.name}</strong>. Copy it now:
        it won’t be shown again.
      </p>
      <code
        data-testid="new-token"
        className="mt-2 block font-mono text-secondary break-all select-all"
      >
        {created.token}
      </code>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <PrimaryButton type="button" onClick={() => void copy()}>
          Copy
        </PrimaryButton>
        <SecondaryButton type="button" onClick={onDone}>
          Done
        </SecondaryButton>
        {copied === 'yes' && <span className="font-mono text-label text-accent">COPIED</span>}
        {copied === 'failed' && (
          <span className="text-secondary text-muted">Copy failed: select the token instead.</span>
        )}
      </div>
    </div>
  );
}

function TokenRow({ token }: { token: WatcherTokenInfo }) {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const revoke = useMutation({
    mutationFn: () =>
      api<RevokeWatcherTokenResponse>(`/api/settings/watcher-tokens/${token.id}`, {
        method: 'DELETE',
      }),
    onSettled: () => {
      setConfirming(false);
      void queryClient.invalidateQueries({ queryKey: queryKeys.watcherTokens });
    },
  });
  const revoked = token.revokedAt !== null;

  return (
    <DataRow columns={COLUMNS}>
      <span role="cell" className={`min-w-0 truncate text-name ${revoked ? 'text-muted' : ''}`}>
        {token.name}
      </span>
      <span role="cell" className="font-mono text-secondary text-muted">
        {formatDate(token.createdAt)}
      </span>
      <span role="cell" className="font-mono text-secondary text-muted">
        {token.lastSeenAt ? formatAge(token.lastSeenAt) : 'never'}
      </span>
      <span
        role="cell"
        className={`font-mono text-secondary ${revoked ? 'text-muted' : 'text-accent'}`}
      >
        {revoked ? 'REVOKED' : 'ACTIVE'}
      </span>
      <span role="cell" className="flex flex-wrap justify-end gap-2">
        {!revoked &&
          (confirming ? (
            <>
              <SecondaryButton
                type="button"
                className="border-danger text-danger hover:text-danger"
                disabled={revoke.isPending}
                onClick={() => revoke.mutate()}
              >
                Confirm revoke
              </SecondaryButton>
              <SecondaryButton type="button" onClick={() => setConfirming(false)}>
                Cancel
              </SecondaryButton>
            </>
          ) : (
            <SecondaryButton type="button" onClick={() => setConfirming(true)}>
              Revoke
            </SecondaryButton>
          ))}
      </span>
    </DataRow>
  );
}
